import { type Hex, hexKey } from '../core/hex';
import type { Tile, TileMutations } from '../core/tile';
import { CHUNK_SIZE, type Chunk, type ChunkCoord, chunkCoordOf, chunkKey } from '../core/world';
import type { TileSource } from '../movement/Movement';
import type { Persistence } from '../persistence/Persistence';
import type { ChunkedWorldGen } from '../worldgen/ChunkedWorldGen';

/**
 * WorldStore composes lazy chunked generation, persistence, and an in-memory
 * tile cache into a single `TileSource` (per HEX-9 Movement contract).
 *
 *   - `getTileAt` is sync — Movement and Vision query it on the hot path. It
 *     returns `null` when the chunk is not yet in the cache; callers (App)
 *     are expected to have called `ensureAround` first.
 *   - `ensureAround(centre, radius)` is the async warm pass: load every chunk
 *     overlapping the radius around `centre` from persistence, generate +
 *     persist any missing chunk, and overlay any persisted tile mutations onto
 *     the chunk's regenerated tiles (HEX-29 / ADR-0012 §8).
 *
 * The cache stores `Tile` references the rest of the loop (lifecycle, hover,
 * renderer) shares — `TileLifecycle.onCross` mutates the same object the
 * renderer holds, so face flips are observed everywhere without a re-fetch.
 */
export class WorldStore implements TileSource {
  private readonly chunkByKey = new Map<string, Chunk>();
  private readonly tileByKey = new Map<string, Tile>();

  constructor(
    private readonly seed: number,
    private readonly persistence: Pick<
      Persistence,
      'saveChunk' | 'loadChunk' | 'loadTileMutationsForChunk'
    >,
    private readonly gen: ChunkedWorldGen,
  ) {}

  getTileAt(hex: Hex): Tile | null {
    return this.tileByKey.get(hexKey(hex)) ?? null;
  }

  /**
   * Ensure every chunk overlapping `radius` axial steps of `centre` is loaded
   * into the cache. Returns the cached tiles within that radius (deduplicated)
   * for the caller to feed to the renderer.
   */
  async ensureAround(centre: Hex, radius: number): Promise<Tile[]> {
    const coords = chunkCoordsAround(centre, radius);
    for (const coord of coords) {
      await this.ensureChunk(coord);
    }
    const out: Tile[] = [];
    for (let dq = -radius; dq <= radius; dq++) {
      const drMin = Math.max(-radius, -dq - radius);
      const drMax = Math.min(radius, -dq + radius);
      for (let dr = drMin; dr <= drMax; dr++) {
        const tile = this.tileByKey.get(hexKey({ q: centre.q + dq, r: centre.r + dr }));
        if (tile) out.push(tile);
      }
    }
    return out;
  }

  /** Tiles currently in the cache. Exposed for tests and renderer warm-up. */
  cachedTiles(): readonly Tile[] {
    return [...this.tileByKey.values()];
  }

  private async ensureChunk(coord: ChunkCoord): Promise<void> {
    const key = chunkKey(coord);
    if (this.chunkByKey.has(key)) return;

    let chunk = await this.persistence.loadChunk(coord);
    if (!chunk) {
      chunk = this.gen.generateChunk(coord, this.seed);
      await this.persistence.saveChunk(coord, chunk);
    }

    const mutations = await this.persistence.loadTileMutationsForChunk(coord);
    for (const tile of chunk.tiles) {
      const key = hexKey({ q: tile.q, r: tile.r });
      const overlay = mutations.get(key);
      const merged: Tile = overlay ? overlayMutations(tile, overlay) : tile;
      this.tileByKey.set(key, merged);
    }
    this.chunkByKey.set(key, chunk);
  }
}

function overlayMutations(tile: Tile, overlay: TileMutations): Tile {
  // Activated pillar slot (HEX-122): a ground tile whose lifecycle_state was
  // set to 'activatedPillarSlot' by town liberation. Upgrade the type in memory
  // so the live session and every reload see a functional pillar without touching
  // the immutable chunk blob.
  const isActivatedPillar =
    tile.type === 'ground' && overlay.lifecycleState?.kind === 'activatedPillarSlot';
  return {
    ...tile,
    type: isActivatedPillar ? 'pillar' : tile.type,
    state: deriveState(tile, overlay.metadata),
    metadata: overlay.metadata,
    crossings: overlay.crossings,
    lifecycleState: isActivatedPillar ? null : overlay.lifecycleState,
    roadNetworkId: overlay.roadNetworkId,
  };
}

/**
 * If a discoverable tile already has a `discoveredBy` set in persisted
 * metadata, it's at least `revealed` — restore that on reload so the renderer
 * doesn't show the placeholder face for an already-revealed tile.
 */
function deriveState(tile: Tile, metadata: { discoveredBy: string | null }): Tile['state'] {
  if (metadata.discoveredBy === null) return tile.state;
  if (tile.state === 'unrevealed') return 'revealed';
  return tile.state;
}

function chunkCoordsAround(centre: Hex, radius: number): ChunkCoord[] {
  const seen = new Set<string>();
  const out: ChunkCoord[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    const drMin = Math.max(-radius, -dq - radius);
    const drMax = Math.min(radius, -dq + radius);
    for (let dr = drMin; dr <= drMax; dr++) {
      const coord = chunkCoordOf(centre.q + dq, centre.r + dr);
      const key = `${coord.cq},${coord.cr}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(coord);
    }
  }
  return out;
}

/** Exposed for tests — chunk size knowledge stays local-source-of-truth in `core/world`. */
export const WORLD_CHUNK_SIZE = CHUNK_SIZE;
